import type { FC } from 'react';
import { cn } from '@components/lib/utils';
import { Show } from '@components/Show';
import type { EvalRunDetail, EvalRunStep } from '@queries/evalRuns';
import type { LucideIcon } from 'lucide-react';
import { CircleCheck, CircleX, Clock, Loader, LoaderCircle } from 'lucide-react';

import { STEP_STATUS_LABEL } from '../constants';
import type { EvalStepStatus } from '../types';
import { RUN_IS_TERMINAL } from '../types';
import { lensDisplayLabel, lensHint } from '../utils/lenses';

import { GuidanceHero } from './GuidanceHero';

const isStepDone = (step: EvalRunStep) => step.status === 'SUCCEEDED' || step.status === 'FAILED';

interface ScanProgressProps {
  detail: EvalRunDetail;
}

/** The drawer body while a scan is in flight: overall progress plus one card per lens. */
export const ScanProgress: FC<ScanProgressProps> = ({ detail }) => {
  const { run, steps } = detail;
  const total = run.total_steps || steps.length;
  const done = steps.filter(isStepDone).length;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  // Every lens is in, but the run is not: consolidation is merging the
  // duplicates and the finalize step has not written the result yet. Say so,
  // or 100% with nothing happening reads as stuck.
  const isConsolidating = total > 0 && done >= total && !RUN_IS_TERMINAL[run.status];
  // Before the plan step has written the lens rows there is nothing to
  // count: show a preparing state instead of "0 of ?" over an empty list.
  const isPreparing = steps.length === 0;

  return (
    <div data-testid="pbqa-scan-progress">
      <GuidanceHero title="Scanning your contract">
        This usually takes a few minutes. You can close this panel and keep working, and we will tell you when your
        guidance is ready.
      </GuidanceHero>
      <div className="px-6">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600" data-testid="pbqa-scan-progress-label">
            {isPreparing ? 'Preparing the scan…' : `${done} of ${total} lenses scanned`}
          </span>
          <Show when={!isPreparing}>
            <span className="font-semibold text-gray-900 tabular-nums">{pct}%</span>
          </Show>
        </div>
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-gray-200">
          <Show when={isPreparing}>
            <div className="h-full w-1/3 animate-pulse rounded-full bg-violet-400" data-testid="pbqa-scan-preparing" />
          </Show>
          <Show when={!isPreparing}>
            <div
              className={cn('h-full rounded-full transition-all', pct === 100 ? 'bg-teal-500' : 'bg-violet-600')}
              style={{ width: `${pct}%` }}
            />
          </Show>
        </div>
        <Show when={isConsolidating}>
          <p
            className="mt-2 flex items-center gap-1.5 text-xs text-gray-500"
            data-testid="pbqa-scan-consolidating"
            role="status"
          >
            <LoaderCircle className="size-3.5 animate-spin" aria-hidden />
            Consolidating suggestions…
          </p>
        </Show>
        <Show when={Boolean(run.error_message)}>
          <p className="bg-error-50 text-error-700 mt-3 rounded-md px-3 py-2 text-xs" role="alert">
            {run.error_message}
          </p>
        </Show>
        <ul className="mt-5 flex flex-col gap-2.5 pb-6">
          {isPreparing
            ? PLACEHOLDER_ROWS.map((row) => <LensPlaceholder key={row} />)
            : steps.map((step) => <LensCard key={step.id} step={step} />)}
        </ul>
      </div>
    </div>
  );
};

/** Stand-ins for the lens cards while the plan step is still writing them. */
const PLACEHOLDER_ROWS = [0, 1, 2, 3, 4];

const LensPlaceholder: FC = () => (
  <li
    className="flex items-start gap-2.5 rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 shadow-xs"
    aria-hidden
  >
    <span className="mt-0.5 size-4 shrink-0 animate-pulse rounded bg-gray-200" />
    <span className="flex min-w-0 flex-1 flex-col gap-1.5">
      <span className="h-3 w-28 animate-pulse rounded bg-gray-200" />
      <span className="h-2.5 w-48 animate-pulse rounded bg-gray-100" />
    </span>
    <span className="mt-0.5 size-4 shrink-0 animate-pulse rounded-full bg-gray-100" />
  </li>
);

const STATUS_ICON: Record<EvalStepStatus, { Icon: LucideIcon; className: string }> = {
  PENDING: { Icon: Clock, className: 'text-gray-400' },
  RUNNING: { Icon: Loader, className: 'animate-spin text-violet-600' },
  SUCCEEDED: { Icon: CircleCheck, className: 'text-teal-600' },
  FAILED: { Icon: CircleX, className: 'text-error-600' },
};

/** The lens's own icon follows the status too: grey queued, violet running, green done. */
const LENS_ICON_TONE: Record<EvalStepStatus, string> = {
  PENDING: 'text-gray-400',
  RUNNING: 'text-violet-600',
  SUCCEEDED: 'text-teal-600',
  FAILED: 'text-error-600',
};

/** Thin progress rule along the card's bottom edge. */
const STATUS_BAR: Record<EvalStepStatus, string> = {
  PENDING: 'hidden',
  RUNNING: 'w-2/5 animate-pulse bg-violet-600',
  SUCCEEDED: 'w-full bg-teal-500',
  FAILED: 'w-full bg-error-500',
};

const LensCard: FC<{ step: EvalRunStep }> = ({ step }) => {
  const { Icon, description } = lensHint(step.step_key, step.step_label);
  const label = lensDisplayLabel(step.step_key, step.step_label);
  const { Icon: StatusIcon, className: statusClasses } = STATUS_ICON[step.status];

  return (
    <li
      className="relative overflow-hidden rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 shadow-xs"
      data-testid={`pbqa-lens-${step.step_key}`}
    >
      <div className="flex items-start gap-2.5">
        <Icon className={cn('mt-0.5 size-4 shrink-0', LENS_ICON_TONE[step.status])} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] leading-[18px] font-semibold text-gray-900">{label}</p>
          {description && <p className="mt-0.5 text-xs leading-4 text-gray-500">{description}</p>}
        </div>
        <StatusIcon
          className={cn('mt-0.5 size-4 shrink-0', statusClasses)}
          role="img"
          aria-label={STEP_STATUS_LABEL[step.status]}
        />
      </div>
      <div className={cn('absolute inset-x-0 bottom-0 h-0.5', STATUS_BAR[step.status])} aria-hidden />
    </li>
  );
};
